import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { api } from "../lib/api";
import {
  clearNewTicketDraft,
  loadNewTicketDraft,
  newTicketDraftId,
  saveNewTicketDraft,
} from "../lib/drafts";
import { queryKeys } from "../lib/queryKeys";
import { RecipientInput, type Recipient } from "./RecipientInput";
import { ComposerTrailer } from "./ComposerTrailer";
import { Button } from "./ui/Button";
import { Overlay } from "./ui/Overlay";
import { cn } from "./ui/cn";
import {
  MessageEditor,
  editorText,
  type PendingUpload,
} from "./editor/MessageEditor";
import { SubjectHint, SubjectInput } from "./editor/SubjectInput";

/**
 * Raise a ticket the desk is starting.
 *
 * A dialog rather than a page: composing is a detour from whatever the agent
 * was doing, and a route would lose their place in the queue. Recipient,
 * subject and pipeline are required -- a hand-raised ticket has no inbound mail
 * to classify it, so the classification has to come from the person raising it.
 *
 * The body is optional. A ticket can be raised to track a phone call or an
 * order chase before anything is written, and forcing a message would mean
 * agents typing "n/a" to get past it.
 *
 * ## Why it survives being closed
 *
 * The dialog is dismissed by Escape and by a click on the backdrop, which is
 * correct for an overlay and was quietly expensive here: half a ticket raised
 * against an order number somebody had gone to look up was lost to a stray
 * click outside the box. So everything in it is written to this browser as it
 * is typed, restored when the dialog next opens, and dropped the moment the
 * ticket is actually raised.
 *
 * Closing therefore no longer means discarding -- but nor does it mean the
 * draft is hidden, which would be the worse half of the trade. The Drafts view
 * in the sidebar lists it, so there is somewhere to find it that does not
 * depend on remembering to press this button again.
 */
export function NewTicketDialog({
  draftId,
  initial,
  onClose,
}: {
  /**
   * Which unsent ticket this dialog is writing.
   *
   * Passed when resuming one from the Drafts view. Absent means a new one, and
   * it gets its own id so starting a second ticket does not overwrite the
   * first -- which is the whole point of the store being keyed.
   */
  draftId?: string;
  /**
   * Who and what a new ticket starts with, when it is raised from somewhere
   * that already knows -- the follow-up email off a sales call. Ignored when a
   * draft is being resumed: what somebody typed outranks a guess.
   */
  initial?: { to?: Recipient[]; subject?: string };
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /*
   * Read once, into the initialisers. Restoring in an effect instead would
   * blank a field the agent had already started typing into, because the
   * dialog is usable before the effect runs.
   */
  const [id] = useState(() => draftId ?? newTicketDraftId());
  const [restored] = useState(() => (draftId ? loadNewTicketDraft(draftId) : null));

  /*
   * Whether the ticket went out, checked by the autosave below.
   *
   * A ref and not state, because the thing that has to read it is the effect's
   * cleanup -- which runs on unmount, against the closure of the last render.
   * Emptying the fields on success would not reach it: `onClose` unmounts this
   * component in the same batch, so there is no later render for the cleanup
   * to close over and it would write the ticket it had just sent back out as
   * a draft.
   */
  const sent = useRef(false);

  const [to, setTo] = useState<Recipient[]>(restored?.to ?? initial?.to ?? []);
  const [toName, setToName] = useState(restored?.toName ?? "");
  const [subject, setSubject] = useState(restored?.subject ?? initial?.subject ?? "");
  const [pipelineId, setPipelineId] = useState(restored?.pipelineId ?? "");
  const [channelId, setChannelId] = useState(restored?.channelId ?? "");
  const [cc, setCc] = useState<Recipient[]>(restored?.cc ?? []);
  const [showCc, setShowCc] = useState(restored?.showCc ?? false);
  const [body, setBody] = useState(restored?.body ?? "");
  const [uploads, setUploads] = useState<PendingUpload[]>(
    restored?.uploads ?? [],
  );
  const [orderNumber, setOrderNumber] = useState(restored?.orderNumber ?? "");
  const [quoteNumber, setQuoteNumber] = useState(restored?.quoteNumber ?? "");
  const [returnsReference, setReturnsReference] = useState(
    restored?.returnsReference ?? "",
  );
  const [laneTask, setLaneTask] = useState(restored?.laneTask ?? "");
  const [error, setError] = useState<string | null>(null);

  const pipelines = useQuery({
    queryKey: queryKeys.pipelines,
    queryFn: api.pipelines,
  });
  const sendFrom = useQuery({
    queryKey: queryKeys.sendFrom,
    queryFn: api.sendFrom,
  });

  // Pre-select the agent's default alias once the list arrives.
  useEffect(() => {
    if (channelId || !sendFrom.data?.length) return;
    const preferred = sendFrom.data.find((entry) => entry.isDefault) ?? sendFrom.data[0];
    if (preferred) setChannelId(String(preferred.id));
  }, [sendFrom.data, channelId]);

  /*
   * Persist on every change, debounced, and once more on the way out.
   *
   * The same shape as the composer's, and for the same reason: this is a
   * synchronous localStorage write on the typing path, and the write that
   * matters most is the one the unmount does -- which is precisely the moment
   * an agent clicked outside and lost the lot.
   */
  useEffect(() => {
    const store = () => {
      if (sent.current) return;
      saveNewTicketDraft(id, {
        to,
        toName,
        subject,
        pipelineId,
        channelId,
        cc,
        showCc,
        body,
        uploads,
        orderNumber,
        quoteNumber,
        returnsReference,
        laneTask,
      });
    };
    const timer = window.setTimeout(store, 400);
    return () => {
      window.clearTimeout(timer);
      store();
    };
  }, [
    to,
    toName,
    subject,
    pipelineId,
    channelId,
    cc,
    showCc,
    body,
    uploads,
    orderNumber,
    quoteNumber,
    returnsReference,
    laneTask,
  ]);

  // Escape closes, matching every other overlay in the app.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  const create = useMutation({
    mutationFn: () =>
      api.createTicket({
        /*
         * The first recipient is the ticket's contact -- a ticket is a
         * conversation with somebody, and that has to be one person. Everyone
         * else is on the To line of the opening email.
         */
        toEmail: recipient!.email,
        toName: toName.trim() || recipient!.name || undefined,
        alsoTo: to.slice(1).length ? to.slice(1) : undefined,
        subject: subject.trim(),
        pipelineId: Number(pipelineId),
        channelId: Number(channelId),
        orderNumber: orderNumber.trim() || null,
        quoteNumber: quoteNumber.trim() || null,
        returnsReference: returnsReference.trim() || null,
        laneTask: laneTask.trim() || null,
        /*
         * Only sent alongside a body: with no opening message there is nothing
         * for a copied recipient to be copied on, and passing them anyway would
         * silently drop them.
         */
        cc: hasBody && cc.length ? cc : undefined,
        bodyHtml: hasBody ? body : undefined,
        attachments: uploads.length ? uploads : undefined,
      }),
    onSuccess: (ticket) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.ticketsRoot });
      void queryClient.invalidateQueries({ queryKey: queryKeys.viewCounts });
      /* Flag first: the autosave's cleanup runs on the unmount `onClose`
         causes, and it is the only other thing that writes this key. */
      sent.current = true;
      clearNewTicketDraft(id);
      onClose();
      navigate(`/v/open/${ticket.id}`);
    },
    onError: (cause: unknown) =>
      setError(cause instanceof Error ? cause.message : "Could not create ticket"),
  });

  const hasBody = editorText(body).length > 0;

  /*
   * The chip is the validation. An address only becomes one by surviving the
   * same extraction the send path uses, so there is nothing left here to check
   * the shape of -- and no way to be holding a half-typed address that looks
   * accepted.
   */
  const recipient = to[0];
  const canCreate =
    recipient && subject.trim() && pipelineId && channelId && !create.isPending;

  return (
    <Overlay
      title="New ticket"
      size="lg"
      onClose={onClose}
      footer={
        <>
          {/* Said beside the button it describes, and it is the whole
              difference between the two things this dialog can do. */}
          <span className="mr-auto text-xs text-faint">
            {hasBody
              ? "The message is emailed immediately."
              : "No email is sent."}
          </span>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate()} disabled={!canCreate}>
            {create.isPending
              ? "Creating…"
              : hasBody
                ? "Create and send"
                : "Create ticket"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-[2fr_1fr]">
          {/*
            The same chip field as Cc, and no longer capped at one: writing
            to three people at a customer is one conversation, not three
            tickets. The first is the ticket's contact -- see the note below
            the field -- and the rest are on the To line of the opening email.
          */}
          <Field label="To" required wrap="div">
            <RecipientInput
              id="new-ticket-to"
              autoFocus
              value={to}
              onChange={(next) => {
                setTo(next);
                /*
                 * A name from a known contact fills the Name box, but never
                 * overwrites one already typed -- somebody who has corrected
                 * a stored name meant the correction.
                 */
                const named = next[0]?.name;
                if (named && !toName.trim()) setToName(named);
              }}
              placeholder="customer@example.com"
            />
          </Field>
          <Field label={to.length > 1 ? "Contact name" : "Name"}>
            <input
              value={toName}
              onChange={(event) => setToName(event.target.value)}
              placeholder="Optional"
              className={inputClass}
            />
          </Field>
        </div>

        {/*
          Said once it can actually matter. A ticket has one contact, so with
          several recipients it is worth knowing which one the ticket is
          filed under -- and that the others are on this email rather than on
          the ticket, so a later reply goes to whoever answers.
        */}
        {to.length > 1 ? (
          <p className="text-xs text-muted">
            This ticket will be filed under {recipient?.name || recipient?.email}.
            The {to.length - 1} other {to.length === 2 ? "address" : "addresses"} are on
            this email only — replies go to whoever writes back.
          </p>
        ) : null}

        {showCc ? (
          <div className="flex items-start gap-2">
            <label
              htmlFor="new-ticket-cc"
              className="mt-1.5 shrink-0 text-xs text-muted"
            >
              Cc
            </label>
            <div className="min-w-0 flex-1">
              <RecipientInput id="new-ticket-cc" autoFocus value={cc} onChange={setCc} />
            </div>
            <button
              type="button"
              onClick={() => {
                setCc([]);
                setShowCc(false);
              }}
              className="mt-1.5 shrink-0 text-xs text-faint hover:text-brand-600 dark:hover:text-brand-400"
              aria-label="Remove CC"
            >
              ✕
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowCc(true)}
            className="text-xs text-muted hover:text-brand-600 dark:hover:text-brand-400"
          >
            + Cc
          </button>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="From" required>
            <select
              value={channelId}
              onChange={(event) => setChannelId(event.target.value)}
              className={inputClass}
            >
              {(sendFrom.data ?? []).map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.displayName} ({entry.address})
                </option>
              ))}
            </select>
          </Field>

          <Field label="Pipeline" required>
            <select
              value={pipelineId}
              onChange={(event) => setPipelineId(event.target.value)}
              className={inputClass}
            >
              <option value="">Choose a pipeline…</option>
              {(pipelines.data ?? []).map((pipeline) => (
                <option key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {/* `#` here works the way it does in the reply box below, against
            the subject half of the canned library. See SubjectInput. */}
        <Field label="Subject" required hint={<SubjectHint />} wrap="div">
          <SubjectInput
            value={subject}
            onChange={setSubject}
            placeholder="What is this about?"
            className={inputClass}
          />
        </Field>

        <Field
          label="Message"
          hint="Optional — leave blank to raise without sending"
          // Not a label: see Field. Wrapping the editor in one made the box
          // impossible to type in unless the pointer was held down.
          wrap="div"
        >
          {/* The same editor as a reply: formatting, canned responses,
              emoji, attachments and drag-and-drop. Starting a conversation
              deserves the same tools as continuing one. */}
          <MessageEditor
            /*
              The draft's own words, which this never passed.
              
              `body` was restored into state and the editor was seeded with
              nothing, so a resumed draft opened with every field filled in and
              an empty writing surface -- and then the first keystroke reported
              the surface's contents and overwrote the restored body with it.
              The text was in the store the whole time, which is exactly why it
              was reported as "the body is still not saving".
            */
            initialHtml={restored?.body}
            onChange={setBody}
            uploads={uploads}
            onUploadsChange={setUploads}
            onError={setError}
            placeholder="Write the first message…  #shortcut inserts a canned response"
            /*
              A first message is outgoing mail and is signed by the same send
              path a reply is, but this dialog never said so -- so the one
              place an agent writes to a customer from scratch was also the
              one place they could not check what would be appended.
            */
            footer={
              <ComposerTrailer
                /* State here is the select's string value; undefined before
                   an alias is chosen, which is the "no channel" the endpoint
                   already handles by falling back to the agent's own. */
                channelId={channelId ? Number(channelId) : undefined}
              />
            }
          />
        </Field>

        <details className="rounded-md border border-line">
          <summary className="cursor-pointer px-3 py-2 text-xs text-muted hover:text-brand-600 dark:hover:text-brand-400">
            References
          </summary>
          <div className="grid gap-3 border-t border-line p-3 sm:grid-cols-2">
            <Field label="Order number">
              <input
                value={orderNumber}
                onChange={(event) => setOrderNumber(event.target.value)}
                placeholder="NXL-12345678"
                className={inputClass}
              />
            </Field>
            <Field label="Quote number">
              <input
                value={quoteNumber}
                onChange={(event) => setQuoteNumber(event.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Returns reference">
              <input
                value={returnsReference}
                onChange={(event) => setReturnsReference(event.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Lane task">
              <input
                value={laneTask}
                onChange={(event) => setLaneTask(event.target.value)}
                className={inputClass}
              />
            </Field>
          </div>
        </details>

        {error ? <p className="text-xs text-red-600">{error}</p> : null}

        {/*
          Said rather than silently done. Copied recipients only mean anything
          alongside a message, so with no body they are dropped -- and a chip
          sitting in the field while that happens invisibly is exactly the
          kind of thing that gets discovered a week later.
        */}
        {cc.length > 0 && !hasBody ? (
          <p className="text-xs text-amber-600">
            Nothing is emailed without a message, so the copied{" "}
            {cc.length === 1 ? "recipient" : "recipients"} will not be
            included. Write a message to copy them in.
          </p>
        ) : null}

      </div>
    </Overlay>
  );
}

const inputClass = cn(
  "w-full rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm text-content",
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
);

/**
 * One labelled field.
 *
 * `wrap` exists because a <label> with no `for` claims the first labelable
 * element inside it, and clicking anywhere in the label then activates that
 * element. Around the message editor the first one is the toolbar's Bold
 * button, so every click in the message box was forwarded to Bold -- which
 * took the focus the contentEditable had just been given.
 *
 * That is why the box could only be typed into while the pointer was held
 * down: mousedown focused the editor, and the click that completed on release
 * handed focus straight to a button. Anything that is not a single plain
 * control passes wrap="div" and gets a heading instead of a label.
 */
function Field({
  label,
  hint,
  required,
  wrap = "label",
  children,
}: {
  label: string;
  /* A node, not a string: the subject field's hint is a component that hides
     itself when the desk has written no subject templates. */
  hint?: React.ReactNode;
  required?: boolean;
  wrap?: "label" | "div";
  children: React.ReactNode;
}) {
  const Tag = wrap;

  return (
    <Tag className="block">
      <span className="mb-1 flex items-baseline gap-1.5 text-xs text-muted">
        {label}
        {required ? <span className="text-brand-600">*</span> : null}
        {hint ? <span className="text-faint">{hint}</span> : null}
      </span>
      {children}
    </Tag>
  );
}

